import styled from 'styled-components';

type ContainerProps = {
  width?: string;
};

type InputContainerProps = {
  padding?: string;
  borderColor?: string;
  inputHeight?: string;
  inputWidth?: string;
  $isReadOnly?: boolean;
  $readOnlyMuted?: boolean;
  $isInactive?: boolean;
  $mutedWhenInactive?: boolean;
  $isCompact?: boolean;
  $hasEndAdornment?: boolean;
};

type InputProps = {
  padding?: string;
  $isReadOnly?: boolean;
  $isInactive?: boolean;
  $isCompact?: boolean;
};

const getTextColor = (isReadOnly?: boolean) =>
  isReadOnly ? '#5e5e60' : '#383839';

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: ${({ width }) => width ?? '100%'};
`;

export const Label = styled.label<{ $hidden?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 19.6px;
  color: #131313;
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;

  ${({ $hidden }) =>
    $hidden &&
    `
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    min-height: 0;
  `}
`;

export const LabelText = styled.span`
  font-weight: 500;
`;

export const RequiredMark = styled.span`
  color: #d83143;
  font-weight: 500;
`;

export const InputContainer = styled.div<InputContainerProps>`
  border: 1px solid
    ${({ borderColor, $isReadOnly, $readOnlyMuted = true }) => {
      if ($isReadOnly && $readOnlyMuted) {
        return '#e7e8ec';
      }

      return borderColor ? borderColor : '#ceced2';
    }};
  position: relative;
  border-radius: 5px;
  overflow: hidden;
  padding: ${({ padding, $isInactive, $isCompact, inputHeight }) => {
    if (padding) {
      return padding;
    }

    if ($isInactive || $isCompact || inputHeight === '40px') {
      return '0 12px';
    }

    return '4px 12px';
  }};
  background-color: ${({
    $isReadOnly,
    $readOnlyMuted = true,
    $isInactive,
    $mutedWhenInactive,
  }) => {
    if ($isReadOnly && $readOnlyMuted) {
      return '#f7f8fa';
    }

    if ($isInactive && $mutedWhenInactive) {
      return '#f5f5f5';
    }

    return '#fff';
  }};
  display: flex;
  align-items: ${({ $hasEndAdornment }) => ($hasEndAdornment ? 'center' : 'stretch')};
  gap: 10px;
  height: ${({ inputHeight }) => (inputHeight ? inputHeight : '50px')};
  width: ${({ inputWidth }) => (inputWidth ? inputWidth : '100%')};
`;

export const Input = styled.input<InputProps>`
  width: 100%;
  height: ${({ $isInactive, $isCompact }) =>
    $isInactive || $isCompact ? '100%' : '40px'};
  font-size: 14px;
  font-family: 'Noto Sans' !important;
  font-weight: 400;
  line-height: 1.4;
  padding: ${({ padding }) => (padding ? padding : '0')};
  border: 0;
  outline: none;
  background-color: transparent;
  color: ${({ $isReadOnly }) => getTextColor($isReadOnly)};

  &:disabled,
  &:read-only {
    background-color: transparent;
    opacity: 1;
    cursor: default;
  }

  &:disabled {
    color: #383839;
    -webkit-text-fill-color: #383839;
  }

  &:read-only {
    color: ${({ $isReadOnly }) => getTextColor($isReadOnly)};
    -webkit-text-fill-color: ${({ $isReadOnly }) => getTextColor($isReadOnly)};
  }
`;

export const EndAdornment = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  pointer-events: none;
`;
